import React, { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { useOidcConfigurationName } from '../hooks/useOidcConfigurationName';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { db } from "../database/db";
import { PersistenceAPI, PersistenceThread, PersistenceMessage } from '../functions/requests/persistence';
import {
    Typography,
    CircularProgress,
    Dialog,
    DialogContent,
    Button,
    DialogActions,
    DialogTitle
} from '@mui/material';

// Extend the Window interface to make TypeScript happy
declare global {
  interface Window {
    openMigration: () => void;
    showMigrationDialog?: boolean;
  }
}

// Create a context to track migration state
export const MigrationContext = React.createContext<{
  isMigrationActive: boolean;
  triggerMigration: () => void;
}>({
  isMigrationActive: false,
  triggerMigration: () => {}
});

// Add a global function to open the dialog
// TypeScript might complain, but this will work at runtime
window.openMigration = () => {
    console.log("Global openMigration called");
    const event = new CustomEvent('force-open-migration-dialog');
    window.dispatchEvent(event);
};

export const MigrationAssistantModal: React.FC = () => {
    const { oidcConfigurationName } = useOidcConfigurationName();
    const oidcAccessToken = useOidcAccessToken(oidcConfigurationName);
    const [isOpen, setIsOpen] = useState(false);
    const [currentConversationName, setCurrentConversationName] = useState<string | null>(null);
    const [isMigrating, setIsMigrating] = useState(false);
    const [userResponded, setUserResponded] = useState(false);
    const [showMigrationDialog, setShowMigrationDialog] = useState(true);
    const queryClient = useQueryClient();

    const conversations = useLiveQuery(() => db.conversationInfo.toArray());
    const messages = useLiveQuery(() => db.chatMessage.toArray());

    // Function to directly check database tables
    const checkDatabaseTables = async () => {
        try {
            // Direct database checks to provide more diagnostics
            const conversationCount = await db.conversationInfo.count();
            const messageCount = await db.chatMessage.count();
            
            console.log("Database direct check results:", {
                conversationCount,
                messageCount
            });
            
            if (conversationCount > 0) {
                const sampleConversation = await db.conversationInfo.limit(1).toArray();
                console.log("Sample conversation:", sampleConversation[0]);
                
                if (sampleConversation.length > 0) {
                    const relatedMessages = await db.chatMessage
                        .where('conversationId')
                        .equals(sampleConversation[0].id)
                        .toArray();
                    
                    console.log(`Found ${relatedMessages.length} messages for conversation ${sampleConversation[0].id}`);
                    if (relatedMessages.length > 0) {
                        console.log("Sample message:", relatedMessages[0]);
                    }
                }
            }
        } catch (error) {
            console.error("Error checking database:", error);
        }
    };

    useEffect(() => {
        // Check database tables when component mounts
        checkDatabaseTables();
    }, []);

    useEffect(() => {
        // Log diagnostics about what's in IndexedDB
        if (conversations) {
            console.log(`Found ${conversations.length} conversations in IndexedDB via hook`);
            if (conversations.length > 0) {
                console.log("First conversation:", {
                    id: conversations[0].id,
                    name: conversations[0].name,
                    messageCount: messages?.filter(m => m.conversationId === conversations[0].id).length || 0
                });
            }
        } else {
            console.log("Conversations query not ready yet");
        }
    }, [conversations, messages]);

    // Check if threads exist in the backend
    const { data: existingThreads, isLoading: isLoadingThreads } = useQuery(
        ['conversationList'],
        () => {
            if (oidcAccessToken?.accessToken) {
                return PersistenceAPI.getThreads(oidcAccessToken.accessToken);
            }
            return [];
        },
        {
            enabled: !!oidcAccessToken?.accessToken,
            staleTime: 60000,
        }
    );

    // Determine if migration is active (dialog is open or migration in progress)
    const isMigrationActive = isOpen || isMigrating;

    // Function to manually trigger migration from outside this component
    const triggerMigration = () => {
        console.log("Migration triggered manually - forcing dialog to open");
        
        // Force dialog to open with no conditions
        setIsOpen(true);
        setUserResponded(false);
        setShowMigrationDialog(true);
        
        // Use a timeout to ensure state has updated
        setTimeout(() => {
            console.log("Migration dialog state after trigger:", {
                isOpen: true,
                showMigrationDialog: true,
                userResponded: false,
                isMigrating: false
            });
            
            // Force a direct DOM-based approach if the dialog isn't visible
            const event = new CustomEvent('force-open-migration-dialog');
            window.dispatchEvent(event);
        }, 100);
        
        // Force a database check to show in console
        checkDatabaseTables();
    };

    const createThreadMutation = useMutation(
        (thread: PersistenceThread) => PersistenceAPI.createThread(oidcAccessToken.accessToken, thread)
    );

    const deleteAllConversations = async () => {
        if (!conversations) return;

        try {
            setIsMigrating(true);
            console.log("Deleting all conversations from IndexedDB directly");
            
            // Directly clear the IndexedDB tables instead of iterating
            await db.conversationInfo.clear();
            await db.chatMessage.clear();
            
            console.log("Successfully cleared all IndexedDB tables");
        } catch (error) {
            console.error("Error deleting conversations:", error);
        } finally {
            setIsMigrating(false);
            setIsOpen(false);
            setShowMigrationDialog(false);
        }
    };

    const migrateData = async () => {
        if (!conversations || !messages || !oidcAccessToken || conversations.length === 0 || isMigrating) {
            setIsOpen(false);
            return;
        }
        const conversationsToMigrate = [...conversations];
        const messagesToMigrate = [...messages];
        setIsMigrating(true);
        setIsOpen(true);
        try {
            // Process in smaller batches to avoid WebSocket timeouts
            const BATCH_SIZE = 10; // Smaller batch size
            
            for (let c = 0; c < conversationsToMigrate.length; c++) {
                const conversation = conversationsToMigrate[c];
                try {
                    setCurrentConversationName(conversation.name);
                    console.log(`Migrating conversation "${conversation.name}" (${c+1}/${conversationsToMigrate.length})`);
                    
                    const thread: PersistenceThread = {
                        name: conversation.name,
                        created_at: conversation.updated.toISOString(),
                        updated_at: conversation.updated.toISOString()
                    };

                    const createdThread = await createThreadMutation.mutateAsync(thread);
                    console.log(`Created thread with ID: ${createdThread.id}`);

                    const conversationMessages = messagesToMigrate.filter(
                        msg => msg.conversationId === conversation.id
                    );
                    
                    // Split messages into smaller batches
                    const messageBatches = [];
                    for (let i = 0; i < conversationMessages.length; i += BATCH_SIZE) {
                        messageBatches.push(conversationMessages.slice(i, i + BATCH_SIZE));
                    }
                    
                    console.log(`Processing ${messageBatches.length} message batches`);
                    
                    for (let batchIndex = 0; batchIndex < messageBatches.length; batchIndex++) {
                        const batch = messageBatches[batchIndex];
                        const toCreate = [];
                        
                        for (let i = 0; i < batch.length; i++) {
                            const message = batch[i];
                            const persistenceMessage: PersistenceMessage = {
                                thread_id: createdThread.id!,
                                role: message.role.toLowerCase(),
                                text_content: message.content,
                                status: 'completed',
                                created_at: message.updated.toISOString(),
                                has_feedback: message.hasFeedback,
                                model_name: message.model?.internal_name
                            };
                            toCreate.push(persistenceMessage);
                        }

                        if (toCreate.length > 0) {
                            console.log(`Migrating batch ${batchIndex+1}/${messageBatches.length} with ${toCreate.length} messages`);
                            try {
                                const result = await PersistenceAPI.createMessageBulk(oidcAccessToken.accessToken, toCreate);
                                console.log(`Batch ${batchIndex+1} result:`, result);
                            } catch (error) {
                                console.error(`Error migrating batch ${batchIndex+1}:`, error);
                                // Continue with next batch even if this one failed
                            }
                            
                            // Add a small delay between batches to prevent overwhelming the server
                            await new Promise(resolve => setTimeout(resolve, 500));
                        }
                    }

                    // Delete conversation from IndexedDB after successful migration
                    await db.conversationInfo.delete(conversation.id);
                    console.log(`Deleted conversation ${conversation.id} from IndexedDB`);
                    
                } catch (error) {
                    console.error(`Error migrating conversation ${conversation.id}:`, error);
                }
            }
        } finally {
            await queryClient.invalidateQueries(['conversationList']);
            setIsMigrating(false);
            setIsOpen(false);
            setShowMigrationDialog(false);
        }
    };

    useEffect(() => {
        // Only show migration dialog if:
        // 1. User has local conversations to migrate
        // 2. User doesn't already have threads in the backend
        // 3. User hasn't responded to the dialog
        // 4. Dialog is set to be shown
        if (
            conversations && 
            messages && 
            oidcAccessToken && 
            !isMigrating && 
            !userResponded && 
            showMigrationDialog && 
            conversations.length > 0 &&
            !isLoadingThreads && 
            existingThreads && 
            existingThreads.length === 0
        ) {
            console.log("Auto-opening migration dialog");
            setIsOpen(true);
        }
    }, [conversations, messages, oidcAccessToken, isLoadingThreads, existingThreads]);

    // Listen for global events to force open the dialog
    useEffect(() => {
        const forceOpenDialog = () => {
            console.log("Force open dialog event received");
            setIsOpen(true);
            setUserResponded(false);
            setShowMigrationDialog(true);
        };
        
        window.addEventListener('force-open-migration-dialog', forceOpenDialog);
        
        return () => {
            window.removeEventListener('force-open-migration-dialog', forceOpenDialog);
        };
    }, []);

    // Always render the context provider so triggerMigration is available
    return (
        <MigrationContext.Provider value={{ isMigrationActive, triggerMigration }}>
            <Dialog
                open={isOpen}
                disableEscapeKeyDown
                disablePortal={false}
                // Keep high z-index but restore standard dialog size
                sx={{ 
                    zIndex: 9999,
                    '& .MuiDialog-paper': {
                        zIndex: 9999
                    }
                }}
                // Use Material UI's standard sizing
                maxWidth="sm"
                fullWidth={false}
            >
                {isMigrating ? (
                    <DialogContent sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Migrating conversation: {currentConversationName}
                        </Typography>
                        <CircularProgress />
                    </DialogContent>
                ) : (
                    <>
                        <DialogTitle>Cloud Migration</DialogTitle>
                        <DialogContent>
                            <Typography variant="body1">AI Conversations are moving to the cloud-- everything you worked on before is still saved, but in order to keep using your current chats you'll need to have them uploaded to the cloud.</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>The upload may take a long time to complete. If you don't have time for this right now and want to get started with cloud conversations, you may choose to postpone the upload to a later time. Or, if you want a clean slate, you may choose to just delete existing offline conversations and start fresh.</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setUserResponded(true);
                                    setShowMigrationDialog(false);
                                    setIsOpen(false);
                                }}
                            >
                                Later
                            </Button>
                            <Button
                                onClick={deleteAllConversations}
                                color="error"
                            >
                                Delete All
                            </Button>
                            <Button
                                onClick={() => {
                                    setUserResponded(true);
                                    migrateData();
                                }}
                                variant="contained"
                                color="primary"
                            >
                                Migrate Now
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </MigrationContext.Provider>
    );
};